import { showAlert, showResult } from "@erp/shared/utils/dialog";
import { getErrorMessage } from "@erp/shared/utils/error";
import { useMutation } from "@tanstack/vue-query";
import { postCommercialInboundConfirm, postResearchInboundConfirm, } from "@erp/shared/api/generated/wrappers/inbound";
import { useLoginInfo } from "@erp/shared/composables/useLoginInfo";
import { MEMBER_TYPE, } from "@erp/shared/components/ui/Dialog/MemberSearchDialog/member.types";
import { createPhase, setApprovalBody, } from "@erp/shared/components/ui/Approval/utils/set-approval-body";
import { INBOUND_TYPE } from "@erp/shared/types/inbound-type.types";
import { uploadFilesWithMapping, } from "@erp/shared/utils/file/upload-file-with-mapping";
import { FILE_CATEGORY } from "@erp/shared/types/file-category.types";
import { S3_FOLDER } from "@erp/shared/api/generated/wrappers/s3";
import { useInvalidateQueries } from "@erp/shared/composables/useInvalidateQueries";
import { usePageInfo } from "@erp/shared/composables/usePageInfo";
import { useBasicInfo } from "@erp/shared/composables/useBasicInfo";
import { queries } from "@erp/shared/queries";
const useInboundRegisterAction = () => {
    const { id } = useLoginInfo();
    const { invalidateQueries } = useInvalidateQueries();
    const { getProjectId, getSpotId } = useBasicInfo();
    const { isCommercialPage } = usePageInfo();
    /**
     * @description 입고 신청 파일 업로드
     * @param formData 입고 신청 폼 데이터
     */
    const setInboundFile = async (formData) => {
        const uploadTasks = [
            {
                file: formData[FILE_CATEGORY.수입검사서],
                category: FILE_CATEGORY.수입검사서,
                path: S3_FOLDER.INBOUND_RECEIVING_INSPECTION,
            },
            {
                file: formData[FILE_CATEGORY.시험성적서],
                category: FILE_CATEGORY.시험성적서,
                path: S3_FOLDER.INBOUND_INSPECTION_REPORT,
            },
            {
                file: formData[FILE_CATEGORY.기타],
                category: FILE_CATEGORY.기타,
                path: S3_FOLDER.INBOUND_ETC,
            },
        ].filter((task) => task.file);
        const { successfulFiles, failedFiles } = await uploadFilesWithMapping(uploadTasks);
        return { successfulFiles, failedFiles };
    };
    /**
     * @description 입고 신청 요청 Body 설정
     */
    const setPostInboundBody = async ({ inboundType, memberInfo, formData, inboundItems, somethingWrongReason, fileData, projectId, approvalDocumentId, }) => {
        try {
            const creater = id.value;
            const checker = memberInfo.find((member) => member.type === MEMBER_TYPE.확인)?.id;
            const approver = memberInfo.find((member) => member.type === MEMBER_TYPE.승인)?.id;
            if (!approver || !checker) {
                throw new Error("승인자 혹은 확인자 정보가 없습니다.");
            }
            const phase = createPhase(creater, checker, approver);
            const products = inboundType === INBOUND_TYPE.직접입력
                ? inboundItems.map((item) => {
                    const spotId = getSpotId(item.spot);
                    if (!spotId) {
                        throw new Error("장소 정보가 없습니다.");
                    }
                    return {
                        type: item.type,
                        classificationName: item.classification,
                        productCode: item.productCode,
                        name: item.name,
                        model: item.model,
                        spec: item.spec,
                        manufacturerName: item.manufacturer,
                        spotId: spotId,
                        num: item.num,
                        unitPrice: item.unitPrice,
                    };
                })
                : inboundType === INBOUND_TYPE.구매신청
                    ? inboundItems.map((item) => {
                        const spotId = getSpotId(item.spot);
                        if (!spotId) {
                            throw new Error("장소 정보가 없습니다.");
                        }
                        const purchaseItem = item;
                        return {
                            productId: item.id ?? undefined,
                            type: item.type,
                            classificationId: item.classificationId ?? undefined,
                            productCode: item.productCode,
                            name: item.name,
                            model: item.model,
                            spec: item.spec,
                            manufacturerId: item.manufacturerId ?? undefined,
                            spotId: spotId,
                            num: item.num,
                            unitPrice: item.unitPrice,
                            approvalDocumentItemId: purchaseItem.approvalDocumentItemId,
                        };
                    })
                    : inboundItems.map((item) => {
                        const spotId = getSpotId(item.spot);
                        if (!spotId) {
                            throw new Error("장소 정보가 없습니다.");
                        }
                        const isUnlinked = !item.id;
                        const base = {
                            productId: item.id ?? undefined,
                            type: item.type,
                            classificationId: isUnlinked
                                ? undefined
                                : (item.classificationId ?? undefined),
                            classificationName: isUnlinked
                                ? item.classification
                                : undefined,
                            productCode: item.productCode,
                            name: item.name,
                            model: item.model,
                            spec: item.spec,
                            manufacturerId: isUnlinked
                                ? undefined
                                : (item.manufacturerId ?? undefined),
                            manufacturerName: isUnlinked ? item.manufacturer : undefined,
                            spotId: spotId,
                            num: item.num,
                            unitPrice: item.unitPrice,
                        };
                        const purchaseItem = item;
                        const orderId = purchaseItem.orderId;
                        const orderProductId = purchaseItem.orderProductId;
                        return orderId ? { ...base, orderId, orderProductId } : base;
                    });
            const postInboundBody = {
                ...setApprovalBody({
                    phase: phase,
                    memberInfo: memberInfo,
                }),
                addData: inboundType,
                inboundDate: new Date(formData.inboundDate + "T" + formData.inboundTime),
                abnormalReason: somethingWrongReason || null,
                receivingInspectionFile: fileData?.[FILE_CATEGORY.수입검사서]?.s3FileName ||
                    null,
                inspectionReportFile: fileData?.[FILE_CATEGORY.시험성적서]?.s3FileName ||
                    null,
                etcFiles: fileData?.[FILE_CATEGORY.기타]?.s3FileName || [],
                projectId: projectId
                    ? projectId
                    : (getProjectId(formData.projectCode) ?? null),
                approvalDocumentId: approvalDocumentId ?? null,
                note: formData.note,
                products: products,
            };
            return postInboundBody;
        }
        catch (err) {
            throw new Error(getErrorMessage(err));
        }
    };
    /**
     * @description 입고 신청 요청
     */
    const createInboundConfirm = useMutation({
        mutationFn: async ({ body }) => {
            return isCommercialPage.value
                ? await postCommercialInboundConfirm(body)
                : await postResearchInboundConfirm(body);
        },
        onSuccess: () => {
            invalidateQueries([
                queries.inbound._def,
                queries.project._def,
                queries.stock._def,
                queries.product._def,
            ]);
            showResult("입고 신청 완료되었습니다", "완료", false, "확인", "닫기");
        },
        onError: (err) => {
            showAlert(getErrorMessage(err), "입고 신청 실패");
        },
    });
    return {
        setInboundFile,
        setPostInboundBody,
        createInboundConfirm,
    };
};
export default useInboundRegisterAction;
